import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Landmark, Search, Menu, ChevronDown, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BookButton } from "@/components/common/BookButton";
import { NAV, BRAND } from "@/data/site";
import { cn } from "@/lib/utils";

const Logo = ({ onClick }) => (
  <Link
    to="/"
    onClick={onClick}
    data-testid="navbar-logo"
    className="flex items-center gap-3 shrink-0"
  >
    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-charcoal text-gold">
      <Landmark size={20} strokeWidth={1.75} />
    </span>
    <span className="flex flex-col leading-none">
      <span className="font-serif text-lg font-semibold tracking-tight text-charcoal">
        Roman Colosseum
      </span>
      <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-gold-dark">
        Tickets
      </span>
    </span>
  </Link>
);

export const Navbar = ({ onOpenSearch }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 border-b transition-all duration-300",
        scrolled
          ? "border-stone-line bg-travertine/90 backdrop-blur-xl"
          : "border-transparent bg-travertine"
      )}
      data-testid="navbar"
    >
      <div className="container-wide flex h-[72px] items-center justify-between gap-6">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setOpenMenu(null)}>
          {NAV.map((item, idx) => (
            <div key={item.label} className="relative" onMouseEnter={() => setOpenMenu(idx)} onFocus={() => setOpenMenu(idx)}>
              <NavLink
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors",
                    isActive || openMenu === idx
                      ? "text-gold-dark"
                      : "text-charcoal hover:text-gold-dark"
                  )
                }
              >
                {item.label}
                <ChevronDown
                  size={14}
                  className={cn(
                    "transition-transform duration-200",
                    openMenu === idx && "rotate-180"
                  )}
                />
              </NavLink>

              {openMenu === idx && (
                <div className="absolute left-0 top-full pt-3">
                  <div className="w-[460px] animate-fade-up rounded-2xl border border-stone-line bg-white p-6 shadow-lift">
                    <div className="grid grid-cols-2 gap-6">
                      {item.columns.map((col) => (
                        <div key={col.heading}>
                          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-slatey">
                            {col.heading}
                          </p>
                          <ul className="space-y-1">
                            {col.links.map((link) => (
                              <li key={link.to}>
                                <Link
                                  to={link.to}
                                  className="block rounded-lg px-3 py-2 text-sm font-medium text-charcoal transition-colors hover:bg-sand hover:text-gold-dark"
                                >
                                  {link.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <Link
                      to={item.to}
                      className="mt-4 flex items-center gap-1.5 border-t border-stone-line pt-4 text-sm font-medium text-gold-dark hover:gap-2.5 transition-all"
                    >
                      View all {item.label} <ChevronRight size={15} />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={onOpenSearch}
            data-testid="navbar-search-button"
            aria-label="Search the site"
            className="flex h-10 items-center gap-2 rounded-full border border-stone-line bg-white px-3.5 text-sm text-slatey transition-colors hover:border-gold hover:text-gold-dark"
          >
            <Search size={16} />
            <span className="hidden xl:inline">Search</span>
            <kbd className="hidden rounded border border-stone-line bg-sand px-1.5 py-0.5 text-[10px] font-medium xl:inline">
              ⌘K
            </kbd>
          </button>

          <BookButton label="Book Now" size="sm" className="hidden sm:inline-flex" testId="navbar-book-now" />

          {/* Mobile menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-line bg-white text-charcoal lg:hidden"
                data-testid="navbar-mobile-toggle"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm overflow-y-auto bg-travertine p-0">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <SheetDescription className="sr-only">Browse tickets, planning guides, highlights and visitor guides</SheetDescription>
              <div className="border-b border-stone-line p-5">
                <Logo onClick={() => setMobileOpen(false)} />
              </div>
              <div className="p-5">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    onOpenSearch();
                  }}
                  className="mb-5 flex w-full items-center gap-2 rounded-xl border border-stone-line bg-white px-4 py-3 text-sm text-slatey"
                  data-testid="mobile-search-button"
                >
                  <Search size={16} /> Search tickets, guides & more
                </button>

                <Accordion type="multiple" className="w-full">
                  {NAV.map((item, idx) => (
                    <AccordionItem key={item.label} value={`m-${idx}`} className="border-b border-stone-line">
                      <AccordionTrigger className="py-4 font-serif text-lg font-medium text-charcoal hover:no-underline">
                        {item.label}
                      </AccordionTrigger>
                      <AccordionContent>
                        <Link
                          to={item.to}
                          className="mb-2 block text-sm font-semibold text-gold-dark"
                        >
                          View all {item.label}
                        </Link>
                        <div className="space-y-3">
                          {item.columns.map((col) => (
                            <div key={col.heading}>
                              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slatey">
                                {col.heading}
                              </p>
                              <ul>
                                {col.links.map((link) => (
                                  <li key={link.to}>
                                    <Link
                                      to={link.to}
                                      className="block py-1.5 text-[15px] text-charcoal"
                                    >
                                      {link.label}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                <BookButton label="Book Now on GetYourGuide" className="mt-6 w-full" testId="mobile-book-now" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
